import {
  Box,
  Button,
  Flex,
  Heading,
  Loading,
  Message,
  Text,
} from '@workwhile/ui'
import React, { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { filtersFormSchema, type FiltersFormValues } from '../filtersSchema'
import { useEventLogsContextValue } from '../context/EventLogsProvider'
import { EventLogsFilters } from './EventLogsFilters'
import { EventLogsTable } from './EventLogsTable'

export const EventLogsMain = () => {
  const {
    state: {
      filterValues,
      items,
      hasNextPage,
      isPending,
      isFetchingNextPage,
      isError,
      errorMessage,
      awaitingFiltersMessage,
    },
    actions: { applyFilters, loadMore },
  } = useEventLogsContextValue()

  const methods = useForm<FiltersFormValues>({
    resolver: zodResolver(filtersFormSchema),
    defaultValues: filterValues,
  })
  const {
    reset,
    formState: { errors },
  } = methods

  // Sync form values to URL changes (e.g. back/forward nav); `keepErrors`/`keepIsSubmitted` preserves the validation summary across the URL→form roundtrip after an invalid submit.
  useEffect(() => {
    reset(filterValues, { keepErrors: true, keepIsSubmitted: true })
  }, [filterValues, reset])

  // Suppress the data section while the form is invalid so stale cached rows don't sit beneath the validation summary.
  const hasFormErrors = Object.keys(errors).length > 0

  const hasItems = items.length > 0
  const showEmptyState =
    !awaitingFiltersMessage && !isPending && !isError && !hasItems

  return (
    <Box margin={3}>
      <Heading level={1} mb={3}>
        Event logs
      </Heading>

      <FormProvider {...methods}>
        <EventLogsFilters onApply={applyFilters} />
      </FormProvider>

      {hasFormErrors ? null : (
        <Box mt={4}>
          {awaitingFiltersMessage ? (
            <Message variant="info" mb={3}>
              {awaitingFiltersMessage}
            </Message>
          ) : null}
          {isPending ? <Loading /> : null}
          {isError && errorMessage ? (
            <Message variant="error">{errorMessage}</Message>
          ) : null}
          {showEmptyState ? (
            <Text color="muted">No events matched these filters.</Text>
          ) : null}
          {hasItems ? (
            <>
              <EventLogsTable items={items} />
              {hasNextPage ? (
                <Flex mt={3}>
                  <Button
                    variant="secondary"
                    loading={isFetchingNextPage}
                    onClick={loadMore}
                  >
                    Load more
                  </Button>
                </Flex>
              ) : null}
            </>
          ) : null}
        </Box>
      )}
    </Box>
  )
}
